import React from 'react'
import { useTranslation } from 'react-i18next'

import { GlobalModalsType } from '@app/routing/GlobalModals'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionEntity } from '@context/EntityContext'
import { useMenu } from '@context/MenuContext'
import { useModalsContext } from '@context/ModalsContext'
import { useProductReadContext } from '@context/ProductContext'

import { InfoIcon } from '@icons/InfoIcon'

import { Body1 } from '@ui/Typography'

import { isTargetProductRegistered } from '@utils/AccountAccess/accountProductStatuses'
import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { useWindowResize } from '@utils/domUtils'
import { useActionPage } from '@utils/path'

import { isClientOnCoolOff } from '@model/AccountDetailedDto'
import { TickmillProductType } from '@model/TickmillProductType'

import IconButton from '@global/iconButton/IconButton'

import { ToggleSwitch } from '../ToggleSwitch/ToggleSwitch'

import styles from './ProductSwitch.module.scss'

export const useProductSwitchClick = (): (() => void) => {
  const { setActiveModal } = useModalsContext()
  const { isDefaultCFDProductType } = useProductReadContext()
  const { account } = useAccountReadContext()

  const isProductTypeCFD = isDefaultCFDProductType()

  const productSwitchClick = () => {
    if (isClientOnCoolOff(account, isProductTypeCFD)) {
      setActiveModal(GlobalModalsType.ClientCoolOff)
    } else if (!isTargetProductRegistered(account, isProductTypeCFD)) {
      setActiveModal(GlobalModalsType.RegisterProduct)
    } else {
      setActiveModal(GlobalModalsType.ProductSwitch)
    }
  }

  return () => productSwitchClick()
}

interface ProductSwitchProps {
  onChange?: (value: boolean) => void
  className?: string
}

export const useProductSwitch = () => {
  const entity = useSessionEntity()
  const { account } = useAccountReadContext()

  const showProductSelector = account?.visibilityConfiguration.showProductSwitcher

  const isProductSwitchVisible = () => {
    if (
      entity !== TickmillCompaniesEnum.TICKMILL_UK ||
      (entity === TickmillCompaniesEnum.TICKMILL_UK && !showProductSelector)
    ) {
      return false
    }
    return true
  }

  return { isProductSwitchVisible }
}

export const ProductSwitch: React.FC<ProductSwitchProps> = () => {
  const click = useProductSwitchClick()
  const { product } = useProductReadContext()
  const { setActiveModal } = useModalsContext()
  const isActionPage = useActionPage()
  const { isMenuOpen } = useMenu()
  const isMobile = useWindowResize()
  const { isDefaultCFDProductType } = useProductReadContext()
  const isProductTypeCFD = isDefaultCFDProductType()
  const { t } = useTranslation()

  const { isProductSwitchVisible } = useProductSwitch()

  const showCancelActionModal = () => {
    setActiveModal(GlobalModalsType.CancelAction)
  }

  if (!isProductSwitchVisible()) {
    return null
  }

  const handleProductSwitch = () => {
    isActionPage ? showCancelActionModal() : click()
  }

  const infoText = isProductTypeCFD
    ? t('Switch from CFD to Futures')
    : t('Switch from Futures to CFD')

  return (
    <div className={styles.productSwitch}>
      {isMobile && (
        <>
          <IconButton onClick={() => setActiveModal(GlobalModalsType.ProductInfo)}>
            <InfoIcon color={'textInfoMedEm'} />
          </IconButton>
          <Body1 className={styles.textInverseBlackNeutral}>{infoText}</Body1>
        </>
      )}
      <ToggleSwitch
        tabs={[
          {
            id: TickmillProductType.CFD,
            name: 'CFD',
            onClick: handleProductSwitch,
            condition: isMenuOpen || product === TickmillProductType.CFD,
          },
          {
            id: TickmillProductType.ETD,
            name: 'Futures',
            onClick: handleProductSwitch,
            condition: isMenuOpen || product === TickmillProductType.ETD,
          },
        ]}
        activeTab={product}
        initialTab={product}
      />
    </div>
  )
}
